import { motion } from 'framer-motion';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PlusIcon from '~/assets/PlusIcon';
import SendIcon from '~/assets/SendIcon';
import { ConversationSection } from '~/types/api.graphql';

import AddAudiencePopover from './AddAudiencePopover';
import {
  AddAudienceButton,
  AudienceMobileOverlay,
  ButtonWrapper,
  InputActionsRow,
  InputContainer,
  InputWrapper,
  PrivacyDisclaimer,
  SendButton,
} from './AIInsightsSidebar.styles';
import AnimatedInputBorder from './AnimatedInputBorder';
import AudienceMentionInput from './AudienceMentionInput';
import AudiencePickerContent from './AudiencePickerContent';
import { useAIMentionInput } from './useAIMentionInput';
import { useConversationReferenceMentions } from './useConversationReferenceMentions';

export interface AIInsightsSidebarInputHandle {
  focus: () => void;
  reset: () => void;
}

interface AIInsightsSidebarInputProps {
  // Context-derived, passed by parent as stable values
  section: ConversationSection | null;
  isOpen: boolean;
  clickCounterCTA: number;
  // Layout / animation
  isExpanded: boolean;
  isInMobile: boolean;
  openAnimKey: number;
  skipExpandAnimation: boolean;
  inputInternalsVisible: boolean;
  // Disabled flags
  isSending: boolean;
  isStreaming: boolean;
  isSessionPending: boolean;
  isSessionLimitReached: boolean;
  // Optional node rendered inside the input container, above the input box
  // (e.g. the session-limit banner) so it aligns with the input in all modes.
  banner?: React.ReactNode;
  // Submit callback — must be stable (useCallback in parent)
  onSubmit: (prompt: string) => void;
}

type BorderPhase = 'hidden' | 'drawing' | 'visible';

const AIInsightsSidebarInputInner = (
  {
    section,
    isOpen,
    clickCounterCTA,
    isExpanded,
    isInMobile,
    openAnimKey,
    skipExpandAnimation,
    inputInternalsVisible,
    isSending,
    isStreaming,
    isSessionPending,
    isSessionLimitReached,
    banner,
    onSubmit,
  }: AIInsightsSidebarInputProps,
  ref: React.ForwardedRef<AIInsightsSidebarInputHandle>,
) => {
  const { t } = useTranslation();

  const {
    editorRef,
    editorCallbackRef,
    mentionQuery,
    hasContent,
    handleInput,
    handleKeyDown: handleMentionKeyDown,
    handleCopy,
    handleCut,
    handlePaste,
    handleEditorMouseDown,
    insertMention,
    insertReferenceAtCaret,
    saveCaretPosition,
    closeMention,
    serialize,
    reset,
    startTypingPrefill,
  } = useAIMentionInput({
    section,
    animateTyping: true,
    isOpen,
    clickCounterCTA,
  });

  const { results: mentionResults, loading: isLoadingMentions } = useConversationReferenceMentions({
    query: mentionQuery,
  });

  const [isAudiencePopoverOpen, setIsAudiencePopoverOpen] = useState(false);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Border animation state (mirrors the open-animation logic from the parent)
  // ---------------------------------------------------------------------------
  const [borderPhase, setBorderPhase] = useState<BorderPhase>('visible');

  // Replicate the parent's open/close border-phase effect here so it is
  // co-located with the animation logic that depends on it (prefill, etc.).
  useEffect(() => {
    if (isOpen) {
      if (skipExpandAnimation) {
        setBorderPhase('visible');
        return undefined;
      }
      setBorderPhase('hidden');
      const id = setTimeout(() => setBorderPhase('drawing'), 200);
      return () => clearTimeout(id);
    }
    // Sidebar closed — reset to visible so next open starts clean
    setBorderPhase('visible');
    setIsAudiencePopoverOpen(false);
    return undefined;
  }, [isOpen, skipExpandAnimation]);

  const handleBorderAnimComplete = useCallback(() => {
    setBorderPhase('visible');
    setTimeout(() => startTypingPrefill(), 100);
  }, [startTypingPrefill]);

  // ---------------------------------------------------------------------------
  // CTA prefill effect (re-click while sidebar already open)
  // ---------------------------------------------------------------------------
  const prevIsOpenRef = useRef(isOpen);
  const borderPhaseRef = useRef(borderPhase);
  useEffect(() => {
    borderPhaseRef.current = borderPhase;
  }, [borderPhase]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!wasOpen || !isOpen) return undefined;
    // Border animation still running — handleBorderAnimComplete will call startTypingPrefill
    if (borderPhaseRef.current !== 'visible') return undefined;
    const id = setTimeout(() => startTypingPrefill(), 0);
    return () => clearTimeout(id);
  }, [clickCounterCTA, isOpen, startTypingPrefill]);

  // ---------------------------------------------------------------------------
  // Imperative handle
  // ---------------------------------------------------------------------------
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      reset,
    }),
    [editorRef, reset],
  );

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const submit = useCallback(() => {
    // Block submit while agent is responding so Enter can't drop the new input
    if (isSending || isStreaming || isSessionPending || isSessionLimitReached) return;
    const prompt = serialize();
    if (!prompt) return;
    onSubmit(prompt);
    reset();
  }, [isSending, isStreaming, isSessionPending, isSessionLimitReached, serialize, onSubmit, reset]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      handleMentionKeyDown(e, { onSubmit: submit });
    },
    [handleMentionKeyDown, submit],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <motion.div
        key={`input-container-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: inputInternalsVisible ? 1 : 0 }}
        transition={{ duration: 0.5 }}
        style={{ flexShrink: 0 }}
      >
        <InputContainer $isSidebarInCollapsedMode={!isExpanded}>
          {banner}
          <InputWrapper ref={inputWrapperRef} $borderHidden={borderPhase !== 'visible'}>
            {borderPhase === 'drawing' && <AnimatedInputBorder onAnimationComplete={handleBorderAnimComplete} />}
            <AudienceMentionInput
              editorRef={editorCallbackRef}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onCopy={handleCopy}
              onCut={handleCut}
              onPaste={handlePaste}
              onMouseDown={(e) => {
                if (isAudiencePopoverOpen) setIsAudiencePopoverOpen(false);
                handleEditorMouseDown(e);
              }}
              placeholder={t('aiInsights.inputPlaceholder')}
              disabled={isSending || isSessionPending || isSessionLimitReached}
              mentionQuery={mentionQuery}
              mentionResults={mentionResults}
              isLoadingMentions={isLoadingMentions}
              onSelectMention={insertMention}
              onCloseMention={closeMention}
            />
            <InputActionsRow>
              <AddAudienceButton
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveCaretPosition();
                }}
                onClick={() => setIsAudiencePopoverOpen((prev) => !prev)}
              >
                <PlusIcon />
                {t('aiInsights.addAudience')}
              </AddAudienceButton>
              <ButtonWrapper>
                <SendButton
                  onClick={submit}
                  disabled={!hasContent || isSending || isStreaming || isSessionPending || isSessionLimitReached}
                  type="button"
                >
                  <SendIcon />
                </SendButton>
              </ButtonWrapper>
            </InputActionsRow>
          </InputWrapper>
        </InputContainer>
        {!isInMobile && (
          <AddAudiencePopover
            anchorRef={inputWrapperRef}
            isOpen={isAudiencePopoverOpen}
            onClose={() => setIsAudiencePopoverOpen(false)}
            onSelectMention={insertReferenceAtCaret}
          />
        )}
      </motion.div>
      <motion.div
        key={`footer-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{ flexShrink: 0 }}
      >
        <PrivacyDisclaimer>{t('aiInsights.privacyDisclaimer')}</PrivacyDisclaimer>
      </motion.div>
      {isInMobile && isAudiencePopoverOpen && (
        <AudienceMobileOverlay>
          <AudiencePickerContent
            onSelectMention={(r) => {
              insertReferenceAtCaret(r);
              setIsAudiencePopoverOpen(false);
            }}
            onClose={() => setIsAudiencePopoverOpen(false)}
            isMobile
          />
        </AudienceMobileOverlay>
      )}
    </>
  );
};

export default memo(forwardRef<AIInsightsSidebarInputHandle, AIInsightsSidebarInputProps>(AIInsightsSidebarInputInner));
